import {
  BOT_BONUS_ARMY_PHASE_CAPTURE_EAGERNESS_MIN,
  BOT_BONUS_ARMY_PHASE_COIN_RESERVE_MAX,
  BOT_BONUS_ARMY_PHASE_COIN_RESERVE_UNITS,
  BOT_BONUS_ARMY_PHASE_KEY_POINT_FACTOR,
  BOT_BONUS_BARRACKS_NEAR_CAMP,
  BOT_BONUS_CAPTURE_GAIN,
  BOT_BONUS_FORGE_ARSENAL_BOOST,
  BOT_BONUS_FORGE_BARRACKS_CAP,
  BOT_BONUS_FORGE_BARRACKS_SCORE,
  BOT_BONUS_FORGE_CAMP_BOOST,
  BOT_BONUS_FORGE_PLATFORM_QUARTER,
  BOT_BONUS_FORGE_QUARTERS_TARGET,
  BOT_BONUS_FRONTIER_FARM_CAP,
  BOT_BONUS_FRONTIER_FARM_ENEMY_DISTANCE_PER_CELL,
  BOT_BONUS_FRONTIER_GARRISON_HOLD,
  BOT_BONUS_FRONTIER_PULL_GAIN,
  BOT_BONUS_FRONTIER_UNDEFENDED_FARM_FACTOR,
  BOT_BONUS_GARRISON_SIZE_SHARE,
  BOT_BONUS_GARRISON_THREAT_RADIUS,
  BOT_BONUS_MILITARY_GAIN,
  BOT_BONUS_NEW_CITY_OUTWEIGHED_FACTOR,
  BOT_BONUS_NEW_CITY_RISK_ZONE_DISTANCE,
  BOT_BONUS_NEW_CITY_UNIT_DISTANCE_HARD,
  BOT_BONUS_NEW_CITY_UNIT_DISTANCE_HARD_FACTOR,
  BOT_BONUS_NEW_CITY_UNIT_DISTANCE_SOFT,
  BOT_BONUS_NEW_CITY_UNIT_DISTANCE_SOFT_FACTOR,
  BOT_BONUS_REAR_CLUSTER_CAPACITY_TARGET,
  BOT_BONUS_REAR_FARM_HUB,
  BOT_BONUS_REAR_FARM_NEAR_WAREHOUSE,
  BOT_BONUS_REAR_WAREHOUSE_BOOST,
  BOT_BONUS_STAGING_FARM_CAP,
  BOT_BONUS_STRONG_CITIES_MIN_INTENSITY,
  BOT_BONUS_SUPPLY_CHAIN_ROAD,
  BOT_BONUS_TARGET_ENEMY_APPROACH_WEIGHT,
  BOT_BONUS_TARGET_ENEMY_GAIN,
  BOT_BONUS_UNDEFENDED_CITY_BARRACKS_SCORE,
  BOT_BONUS_UNDEFENDED_CITY_FARM_SCORE,
  getBonusArmyPhaseLastRound,
  getBotBonusIntensity,
} from "../../configs/botBonusBehavior";
import {checkIfPlayerBonusApplies} from "../gameState/checkIfPlayerBonusApplies";
import {getPlayerBonusValues} from "../gameState/getPlayerBonusValues";
import {isPreBonusCitiesPlayer, isPreW39FactionPlayer, isPreW40V1Player} from "./abTestGates";
import {FACTIONS} from "../../constants/factions";

/**
 * Поведенческие надбавки бота, играющего с экономическим бонусом
 * (см. configs/botBonusBehavior). null — надбавок нет, бот играет как обычно.
 *
 * null возвращается для всех, кроме ботов с ДЕЙСТВУЮЩИМ ненулевым бонусом:
 * живых игроков (бонус локальной игры получают и они), ботов без бонуса и
 * бонуса уровня «нет» — например, бронзовых «явных» ботов.
 *
 * strongCities — пакет волны 33 («сильные города»: ось удара, кузница, тыловой
 * склад, 1 ферма во фронтире, армейская фаза). Включен только у LARGE/GREAT
 * (интенсивность ≥ BOT_BONUS_STRONG_CITIES_MIN_INTENSITY) и выключен у
 * legacy-группы A/B (isPreBonusCitiesPlayer) — те играют прежним пакетом
 * дословно. Все веса пакета лежат здесь же, чтобы решения читали одну точку.
 */
// Волна 40 (V1): пакет «сильные города» рассчитан на армию из многих отрядов, у
// великанов на алмазе и рубине с ним вдвое меньше и армия, и городов (отчет 37)
const checkIfW40GiantsWithoutStrongCities = (player) => player.factionId === FACTIONS.GIANTS
  && !isPreW39FactionPlayer(player.id)
  && !isPreW40V1Player(player.id);

export const getBotBonusProfile = (player, gameType) => {
  if (!player || !player.isBot || !checkIfPlayerBonusApplies(player, gameType)) {
    return null;
  }

  const bonusValues = getPlayerBonusValues(player);
  const intensity = getBotBonusIntensity(bonusValues);
  if (intensity <= 0) {
    return null;
  }

  return {
    intensity,
    // Бонус выдает дополнительные действия строительства. Дворец не дает
    // НИЧЕГО, кроме такого же действия (см. sharedGameStateReducer), поэтому
    // такому боту он не нужен вовсе — а вот бонусу без действий (маленький)
    // дворец по-прежнему полезен
    hasConstructionActionsBonus: (bonusValues.constructionActionsBonus || 0) > 0,
    militaryMultiplier: 1 + BOT_BONUS_MILITARY_GAIN * intensity,
    captureMultiplier: 1 + BOT_BONUS_CAPTURE_GAIN * intensity,
    frontierPullMultiplier: 1 + BOT_BONUS_FRONTIER_PULL_GAIN * intensity,
    undefendedCityBarracksScore: BOT_BONUS_UNDEFENDED_CITY_BARRACKS_SCORE * intensity,
    undefendedCityFarmScore: BOT_BONUS_UNDEFENDED_CITY_FARM_SCORE * intensity,

    // Волна 33
    strongCities: intensity >= BOT_BONUS_STRONG_CITIES_MIN_INTENSITY && !isPreBonusCitiesPlayer(player.id)
      && !checkIfW40GiantsWithoutStrongCities(player),
    armyPhaseLastRound: getBonusArmyPhaseLastRound(),
    frontierFarmCap: BOT_BONUS_FRONTIER_FARM_CAP,
    stagingFarmCap: BOT_BONUS_STAGING_FARM_CAP,
    frontierFarmEnemyDistancePerCell: BOT_BONUS_FRONTIER_FARM_ENEMY_DISTANCE_PER_CELL,
    frontierUndefendedFarmFactor: BOT_BONUS_FRONTIER_UNDEFENDED_FARM_FACTOR,
    rearFarmNearWarehouse: BOT_BONUS_REAR_FARM_NEAR_WAREHOUSE,
    rearFarmHub: BOT_BONUS_REAR_FARM_HUB,
    rearClusterCapacityTarget: BOT_BONUS_REAR_CLUSTER_CAPACITY_TARGET,
    rearWarehouseBoost: BOT_BONUS_REAR_WAREHOUSE_BOOST * intensity,
    supplyChainRoad: BOT_BONUS_SUPPLY_CHAIN_ROAD * intensity,
    forgeBarracksCap: BOT_BONUS_FORGE_BARRACKS_CAP,
    forgeBarracksScore: BOT_BONUS_FORGE_BARRACKS_SCORE * intensity,
    barracksNearCamp: BOT_BONUS_BARRACKS_NEAR_CAMP * intensity,
    forgeCampBoost: BOT_BONUS_FORGE_CAMP_BOOST * intensity,
    forgeArsenalBoost: BOT_BONUS_FORGE_ARSENAL_BOOST * intensity,
    forgePlatformQuarter: BOT_BONUS_FORGE_PLATFORM_QUARTER * intensity,
    forgeQuartersTarget: BOT_BONUS_FORGE_QUARTERS_TARGET,
    newCityUnitDistanceSoft: BOT_BONUS_NEW_CITY_UNIT_DISTANCE_SOFT,
    newCityUnitDistanceHard: BOT_BONUS_NEW_CITY_UNIT_DISTANCE_HARD,
    newCityUnitDistanceSoftFactor: BOT_BONUS_NEW_CITY_UNIT_DISTANCE_SOFT_FACTOR,
    newCityUnitDistanceHardFactor: BOT_BONUS_NEW_CITY_UNIT_DISTANCE_HARD_FACTOR,
    newCityOutweighedFactor: BOT_BONUS_NEW_CITY_OUTWEIGHED_FACTOR,
    newCityRiskZoneDistance: BOT_BONUS_NEW_CITY_RISK_ZONE_DISTANCE,
    armyPhaseKeyPointFactor: BOT_BONUS_ARMY_PHASE_KEY_POINT_FACTOR,
    armyPhaseCaptureEagernessMin: BOT_BONUS_ARMY_PHASE_CAPTURE_EAGERNESS_MIN,
    targetEnemyWeight: 1 + BOT_BONUS_TARGET_ENEMY_GAIN * intensity,
    targetEnemyApproachWeight: BOT_BONUS_TARGET_ENEMY_APPROACH_WEIGHT * intensity,
    frontierGarrisonHold: BOT_BONUS_FRONTIER_GARRISON_HOLD * intensity,
    garrisonSizeShare: BOT_BONUS_GARRISON_SIZE_SHARE,
    garrisonThreatRadius: BOT_BONUS_GARRISON_THREAT_RADIUS,
    armyPhaseCoinReserveUnits: BOT_BONUS_ARMY_PHASE_COIN_RESERVE_UNITS,
    armyPhaseCoinReserveMax: BOT_BONUS_ARMY_PHASE_COIN_RESERVE_MAX,
  };
};
